import React, { useMemo, useState } from 'react';
import {
  Modal,
  Table,
  Button,
  Tag,
  Typography,
  Input,
  message,
  Popconfirm,
  Tooltip,
} from 'antd';
import { EyeOutlined, CheckOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useCardDetail } from '../../context/CardDetailContext';
import dayjs from 'dayjs';
import maturityScheduleService from '../../service/maturity-schedule-service/MaturitySchedule.service';

const { Text } = Typography;

function formatCardNumber(cardNumber?: string) {
  if (!cardNumber || cardNumber.length < 4) return cardNumber || '';
  return `***${cardNumber.slice(-4)}`;
}

interface WrongPaidMaturityScheduleModalProps {
  open: boolean;
  onClose: () => void;
  loading: boolean;
  schedules: any[];
  role?: string | null; // thêm prop role
}

const statusTextMap: Record<string, string> = {
  PAID: 'Đã nạp đáo',
  COMPLETED: 'Đã hoàn thành',
};

const WrongPaidMaturityScheduleModal: React.FC<
  WrongPaidMaturityScheduleModalProps
> = ({ open, onClose, loading, schedules, role }) => {
  const navigate = useNavigate();
  const { setSelectedCard, setMaturityScheduleId, setMaturityYear } =
    useCardDetail();
  const [localData, setLocalData] = useState<any[]>(schedules);
  React.useEffect(() => {
    setLocalData(schedules);
  }, [schedules]);

  const handleCheck = async (record: any) => {
    try {
      await maturityScheduleService.checkMaturitySchedule(record.id);
      setLocalData((prev) => prev.filter((item) => item.id !== record.id));
      message.success('Đã đánh dấu kiểm tra!');
    } catch (e) {
      message.error('Lỗi khi cập nhật trạng thái kiểm tra!');
    }
  };

  //Filter options
  const cardNumbers = useMemo(
    () =>
      Array.from(
        new Set(
          schedules.map((s) => s.creditCard?.cardNumber).filter(Boolean)
        )
      ).map((n) => ({ text: formatCardNumber(n), value: n })),
    [schedules]
  );
  const cardHolderNames = useMemo(
    () =>
      Array.from(
        new Set(
          schedules.map((s) => s.creditCard?.cardHolderName).filter(Boolean)
        )
      ).map((n) => ({ text: n, value: n })),
    [schedules]
  );
  const customerNames = useMemo(
    () =>
      Array.from(
        new Set(
          schedules.map((s) => s.creditCard?.customer?.name).filter(Boolean)
        )
      ).map((n) => ({ text: n, value: n })),
    [schedules]
  );
  const statusFilters = useMemo(
    () =>
      Array.from(
        new Set(
          schedules.map((s) => s.status).filter(Boolean)
        )
      ).map((n: any) => ({ text: statusTextMap[n] || n, value: n })),
    [schedules]
  );

  const employeeNames = useMemo(
    () => {
      const names = new Set<string>();
      schedules.forEach((s) => {
        if (s.employees && Array.isArray(s.employees)) {
          s.employees.forEach((e: any) => {
            const name = e.fullName || e.name || e.employeeCode;
            if (name) names.add(name);
          });
        }
      });
      return Array.from(names).map((n) => ({ text: n, value: n }));
    },
    [schedules]
  );


  const columns = [
    {
      title: 'Ngày đến hạn',
      dataIndex: 'dueDate',
      key: 'dueDate',
      width: 120,
      render: (d: string) => (
        <Tag color="red">{dayjs(d).format('DD-MM-YYYY')}</Tag>
      ),
      sorter: (a: any, b: any) =>
        dayjs(a.dueDate).unix() - dayjs(b.dueDate).unix(),
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }: any) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Tìm ngày (dd-mm-yyyy)"
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={confirm}
            style={{ width: 180, marginBottom: 8, display: 'block' }}
          />
          <Button
            type="primary"
            onClick={confirm}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Lọc
          </Button>
          <Button onClick={clearFilters} size="small" style={{ width: 90 }}>
            Xóa lọc
          </Button>
        </div>
      ),
      onFilter: (value: any, record: any) =>
        dayjs(record.dueDate).format('DD-MM-YYYY').includes(value),
    },
    {
      title: 'Số thẻ',
      dataIndex: ['creditCard', 'cardNumber'],
      key: 'cardNumber',
      filters: cardNumbers,
      onFilter: (value: any, record: any) =>
        record.creditCard?.cardNumber === value,
      width: 100,
      render: (_: any, r: any) => formatCardNumber(r.creditCard?.cardNumber),
    },
    {
      title: 'Chủ thẻ',
      dataIndex: ['creditCard', 'cardHolderName'],
      key: 'cardHolderName',
      filters: cardHolderNames,
      filterSearch: true,
      onFilter: (value: any, record: any) =>
        record.creditCard?.cardHolderName === value,
      width: 120,
      ellipsis: true,
      render: (_: any, r: any) => r.creditCard?.cardHolderName,
    },
    {
      title: 'Khách hàng',
      dataIndex: ['creditCard', 'customer', 'name'],
      key: 'customer',
      filters: customerNames,
      filterSearch: true,
      onFilter: (value: any, record: any) =>
        record.creditCard?.customer?.name === value,
      width: 120,
      ellipsis: true,
      render: (_: any, r: any) => r.creditCard?.customer?.name,
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      filters: statusFilters,
      onFilter: (value: any, record: any) => record.status === value,
      width: 100,
      render: (s: string) => (
        <Tag color={s === 'PAID' ? 'blue' : 'green'}>
          {statusTextMap[s] || s}
        </Tag>
      ),
    },
    {
      title: 'Số đáo hạn',
      dataIndex: 'repaymentAmount',
      key: 'repaymentAmount',
      sorter: (a: any, b: any) => (a.repaymentAmount || 0) - (b.repaymentAmount || 0),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Tìm số tiền"
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={confirm}
            style={{ width: 150, marginBottom: 8, display: 'block' }}
          />
          <Button type="primary" onClick={confirm} size="small" style={{ width: 90, marginRight: 8 }}>
            Lọc
          </Button>
          <Button onClick={clearFilters} size="small" style={{ width: 90 }}>
            Xóa lọc
          </Button>
        </div>
      ),
      onFilter: (value: any, record: any) => (record.repaymentAmount || '').toString().includes(value),
      width: 120,
      align: 'right' as const,
      render: (v: number) => v?.toLocaleString(),
    },
    {
      title: 'Tổng nạp',
      dataIndex: 'totalDepositAmount',
      key: 'totalDepositAmount',
      sorter: (a: any, b: any) => (a.totalDepositAmount || 0) - (b.totalDepositAmount || 0),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Tìm tổng nạp"
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={confirm}
            style={{ width: 150, marginBottom: 8, display: 'block' }}
          />
          <Button type="primary" onClick={confirm} size="small" style={{ width: 90, marginRight: 8 }}>
            Lọc
          </Button>
          <Button onClick={clearFilters} size="small" style={{ width: 90 }}>
            Xóa lọc
          </Button>
        </div>
      ),
      onFilter: (value: any, record: any) => (record.totalDepositAmount || '').toString().includes(value),
      width: 120,
      align: 'right' as const,
      render: (v: number) => v?.toLocaleString(),
    },
    {
      title: 'Tổng rút',
      dataIndex: 'totalWithdrawalAmount',
      key: 'totalWithdrawalAmount',
      sorter: (a: any, b: any) => (a.totalWithdrawalAmount || 0) - (b.totalWithdrawalAmount || 0),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Tìm tổng rút"
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={confirm}
            style={{ width: 150, marginBottom: 8, display: 'block' }}
          />
          <Button type="primary" onClick={confirm} size="small" style={{ width: 90, marginRight: 8 }}>
            Lọc
          </Button>
          <Button onClick={clearFilters} size="small" style={{ width: 90 }}>
            Xóa lọc
          </Button>
        </div>
      ),
      onFilter: (value: any, record: any) => (record.totalWithdrawalAmount || '').toString().includes(value),
      width: 120,
      align: 'right' as const,
      render: (v: number) => v?.toLocaleString(),
    },

    {
      title: 'Phụ trách',
      dataIndex: 'employees',
      key: 'employees',
      filters: employeeNames,
      filterSearch: true,
      onFilter: (value: any, record: any) => {
        if (!record.employees || !Array.isArray(record.employees)) return false;
        return record.employees.some((e: any) =>
          (e.fullName || e.name || e.employeeCode || '') === value
        );
      },
      width: 150,
      render: (emps: any[]) =>
        Array.isArray(emps)
          ? emps.map((e, idx) => (
            <Tag color="blue" key={e.id || idx} style={{ marginBottom: 4 }}>
              {e.fullName || e.name || e.employeeCode || e.id}
              {e.employeeCode ? ` (${e.employeeCode})` : ''}
            </Tag>
          ))
          : '',
    },
    {
      title: 'Ghi chú',
      dataIndex: 'notes',
      key: 'notes',
      width: 150,
      ellipsis: true,
      render: (n: string) => (n ? <Text type="secondary">{n}</Text> : null),
    },
    {
      title: 'Hành động',
      key: 'action',
      fixed: 'right' as const,
      width: 120,
      render: (_: any, record: any) => (
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          <Tooltip title="Xem chi tiết">
            <Button
              icon={<EyeOutlined />}
              type="link"
              size="small"
              onClick={() => {
                setSelectedCard(record.creditCard);
                setMaturityScheduleId(record.id);
                setMaturityYear(dayjs(record.dueDate).year());
                navigate('/card-detail');
              }}
            />
          </Tooltip>
          {/* Ẩn nút check nếu là employee */}
          {role !== 'ROLE_EMPLOYEE' && (
            <Popconfirm
              title="Đánh dấu đã kiểm tra?"
              onConfirm={() => handleCheck(record)}
              okText="Đã kiểm tra"
              cancelText="Hủy"
            >
              <Tooltip title="Đã kiểm tra">
                <Button
                  icon={<CheckOutlined />}
                  type="primary"
                  ghost
                  size="small"
                />
              </Tooltip>
            </Popconfirm>
          )}
        </div>
      ),
    },
  ];

  const modalBodyRef = React.useRef<HTMLDivElement>(null);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={
        <span style={{ fontWeight: 600, fontSize: 18 }}>
          Kỳ đáo hạn làm sai (nạp/rút sai)
        </span>
      }
      footer={null}
      width={1600}
      bodyStyle={{ padding: 24 }}
    >
      <div
        ref={modalBodyRef}
        style={{ maxHeight: 600, overflowY: 'auto', position: 'relative' }}
      >
        <Table
          columns={columns}
          dataSource={localData.filter((r) => !r.isChecked)}
          loading={loading}
          rowKey={(r) => r.id}
          scroll={{ x: 1600 }}
          sticky={{ offsetHeader: 0, getContainer: () => modalBodyRef.current! }}
          pagination={{
            pageSize: 10,
            showSizeChanger: false,
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
          bordered
          size="middle"
          locale={{ emptyText: 'Không có kỳ đáo hạn nào.' }}
        />
      </div>
    </Modal>
  );
};

export default WrongPaidMaturityScheduleModal;
